import type { IframePoolOrder } from '../api';
import { loadOperatorCurrency } from '../currency';
import { MAX_PENDING_EVENTS } from './constants';
import { resolveTrackingCurrency } from './currency';
import { createGtagProvider } from './providers/gtag';
import { createMetaProvider } from './providers/meta';
import { createRedditProvider } from './providers/reddit';
import type { TrackedOffer, TrackingConfig, TrackingEvent, TrackingEventName, TrackingProvider } from './types';

/** An event before its currency is resolved — that needs the partner's override. */
type DraftEvent = Omit<TrackingEvent, 'currency'>;

let providers: TrackingProvider[] = [];
let currencyOverride: string | null = null;
// null once design_system said this operator has no pixels: nothing to wait for.
let pending: DraftEvent[] | null = [];
const viewedOfferIds = new Set<string>();
const reportedOrderIds = new Set<string>();

function createProviders(config: TrackingConfig): TrackingProvider[] {
  const gtag = createGtagProvider({ ga4MeasurementId: config.ga4MeasurementId, googleAds: config.googleAds });
  return [
    ...(config.metaPixelId ? [createMetaProvider(config.metaPixelId)] : []),
    ...(config.redditPixelId ? [createRedditProvider(config.redditPixelId)] : []),
    ...(gtag ? [gtag] : []),
  ];
}

// trackPurchase runs inside the purchase promise: a throw here would show a
// paid purchase as failed. A vendor script never takes the game down with it.
function safely(run: () => void): void {
  try {
    run();
  } catch {
    // the event is lost, the purchase is not
  }
}

function send(draft: DraftEvent): void {
  if (providers.length === 0) {
    if (pending && pending.length < MAX_PENDING_EVENTS) pending.push(draft);
    return;
  }
  const event: TrackingEvent = {
    ...draft,
    currency: resolveTrackingCurrency(loadOperatorCurrency(), currencyOverride),
  };
  for (const provider of providers) safely(() => provider.track(event));
}

/** Loads the partner's pixels once per page load. design_system can arrive
 *  again (embed sign-in) — the first config wins. */
export function initTracking(config: TrackingConfig | null): void {
  if (providers.length > 0) return;
  if (!config) {
    pending = null;
    return;
  }
  providers = createProviders(config);
  currencyOverride = config.currencyOverride;
  for (const provider of providers) safely(() => provider.install());
  const queued = pending ?? [];
  pending = null;
  queued.forEach(send);
}

function offerDraft(name: TrackingEventName, offer: TrackedOffer, quantity: number): DraftEvent {
  const unitPrice = Number.parseFloat(offer.price);
  return {
    name,
    offerId: String(offer.id),
    offerTitle: offer.title,
    quantity,
    value: Number.isFinite(unitPrice) ? Math.round(unitPrice * quantity * 100) / 100 : null,
    orderId: null,
  };
}

/** A pack page shown — once per pack per page load. */
export function trackViewItem(offer: TrackedOffer): void {
  safely(() => {
    const offerId = String(offer.id);
    if (viewedOfferIds.has(offerId)) return;
    viewedOfferIds.add(offerId);
    send(offerDraft('view_item', offer, 1));
  });
}

export function trackBeginCheckout(offer: TrackedOffer, quantity: number): void {
  safely(() => send(offerDraft('begin_checkout', offer, quantity)));
}

/** A completed purchase. A retry under the same idempotency key returns the
 *  same order, so each order is reported once. */
export function trackPurchase(order: IframePoolOrder): void {
  safely(() => {
    if (reportedOrderIds.has(order.id)) return;
    reportedOrderIds.add(order.id);
    send({
      name: 'purchase',
      offerId: String(order.poolOffer.id),
      offerTitle: order.poolOffer.title,
      quantity: order.quantity,
      value: Number.isFinite(order.totalPrice) ? order.totalPrice : null,
      orderId: order.id,
    });
  });
}

/** Test seam: back to a fresh page load. */
export function resetTracking(): void {
  providers = [];
  currencyOverride = null;
  pending = [];
  viewedOfferIds.clear();
  reportedOrderIds.clear();
}
